import React, { useEffect, useState, useCallback } from 'react';
import InternalLayout from '../../components/InternalLayout';
import { internalApi } from '../../utils/api';

// PRD-008 §7 / AC-7 — timeline-style audit log. Newest-first stack of
// rows; each row collapsible to show the full event payload (details
// JSON, ip_address, user_agent, related session id where applicable).
// Operator actions get a slate-gray badge prefix; member actions get
// no badge.

function relativeAndAbsolute(iso) {
  if (!iso) return { rel: '—', abs: '' };
  const d = new Date(iso);
  return { rel: d.toLocaleString(), abs: d.toISOString() };
}

function ActionBadge({ action, isOperator }) {
  const tone = isOperator ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${tone}`}>
      {action}
    </span>
  );
}

export default function OperatorAudit() {
  const [filters, setFilters] = useState({ action_type: '', actor_is_operator: 'any' });
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ limit: 100, offset: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: pagination.limit, offset: pagination.offset };
      if (filters.action_type) params.action_type = filters.action_type;
      if (filters.actor_is_operator !== 'any') params.actor_is_operator = filters.actor_is_operator;
      const res = await internalApi.audit(params);
      setRows(res.data.events);
      setPagination(res.data.pagination);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [filters.action_type, filters.actor_is_operator, pagination.limit, pagination.offset]);

  useEffect(() => { load(); }, [load]);

  return (
    <InternalLayout pageTitle="Audit log">
      <div className="bg-white rounded-md border border-gray-200 shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
          <input
            type="text"
            placeholder="Action (e.g., operator_workspace_view)"
            value={filters.action_type}
            onChange={(e) => { setFilters((f) => ({ ...f, action_type: e.target.value })); setPagination((p) => ({ ...p, offset: 0 })); }}
            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
          />
          <select
            value={filters.actor_is_operator}
            onChange={(e) => { setFilters((f) => ({ ...f, actor_is_operator: e.target.value })); setPagination((p) => ({ ...p, offset: 0 })); }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
          >
            <option value="any">All actors</option>
            <option value="true">Operator actions only</option>
            <option value="false">Member actions only</option>
          </select>
        </div>

        {error && <div className="px-4 py-2 text-sm text-red-700 bg-red-50 border-b border-red-200">{error}</div>}

        <ul className="divide-y divide-gray-100">
          {loading && <li className="px-4 py-6 text-sm text-gray-600">Loading…</li>}
          {!loading && rows.length === 0 && <li className="px-4 py-6 text-sm text-gray-600 italic">No events match these filters.</li>}
          {!loading && rows.map((r) => {
            const t = relativeAndAbsolute(r.created_at);
            const open = expandedId === r.id;
            return (
              <li key={r.id} className="px-4 py-3">
                <button
                  type="button"
                  onClick={() => setExpandedId(open ? null : r.id)}
                  className="w-full text-left flex items-start gap-3"
                >
                  <span className="text-xs text-gray-600 w-44 flex-shrink-0" title={t.abs}>{t.rel}</span>
                  <ActionBadge action={r.action} isOperator={r.actor_is_operator} />
                  <span className="text-sm text-gray-700 flex-1 truncate">
                    {r.target_entity_type}:{r.target_entity_id}
                  </span>
                  {r.operator_reason && (
                    <span className="text-xs text-gray-600 italic truncate max-w-[40ch]">
                      “{r.operator_reason}”
                    </span>
                  )}
                </button>
                {open && (
                  <div className="mt-3 ml-44 text-xs text-gray-700 bg-gray-50 rounded-md p-3 space-y-1">
                    <div><span className="text-gray-600">Actor:</span> {r.actor_user_id || '—'}{r.actor_is_operator ? ' (operator)' : ''}</div>
                    <div><span className="text-gray-600">Workspace:</span> {r.workspace_id || '—'}</div>
                    {r.effective_workspace_id && (
                      <div><span className="text-gray-600">Effective workspace:</span> {r.effective_workspace_id}</div>
                    )}
                    {r.parent_impersonation_session_id && (
                      <div><span className="text-gray-600">Session:</span> {r.parent_impersonation_session_id}</div>
                    )}
                    <div><span className="text-gray-600">IP:</span> {r.ip_address || '—'} · <span className="text-gray-600">UA:</span> {r.user_agent || '—'}</div>
                    {r.details && Object.keys(r.details).length > 0 && (
                      <pre className="mt-2 bg-white border border-gray-200 rounded p-2 overflow-x-auto">
                        {JSON.stringify(r.details, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
          <span>{pagination.total.toLocaleString()} events</span>
          <div className="flex gap-2">
            <button
              disabled={pagination.offset === 0}
              onClick={() => setPagination((p) => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
            >Previous</button>
            <button
              disabled={pagination.offset + pagination.limit >= pagination.total}
              onClick={() => setPagination((p) => ({ ...p, offset: p.offset + p.limit }))}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
            >Next</button>
          </div>
        </div>
      </div>
    </InternalLayout>
  );
}
